import { curry, without, intersection, isEmpty, not } from "ramda";

export const extractExactAge = (birthday, referenceDate) => {
  var differenceInMilisecond = Date.parse(referenceDate) || Date.now() - Date.parse(birthday);

  var years = Math.floor(differenceInMilisecond / 31536000000);
  var days = Math.floor((differenceInMilisecond % 31536000000) / 86400000);
  var months = Math.floor(days / 30);

  days = days % 30;

  if (isNaN(years) || isNaN(months) || isNaN(days)) {
    return {};
  } else {
    return {
      years,
      months,
      days,
    };
  }
};

// valueOrDefault :: a -> a -> a
export const valueOrDefault = curry(($default, value) => value ?? $default);

// withoutItem :: a -> [a] -> [a]
export const withoutItem = curry((x, xs) => xs |> without([x]));

export const intersect = (needed = [], received = []) => intersection(needed, received) |> isEmpty |> not;

export const isNullOrWhitespace = (str) => !str || /^\s*$/.test(str);

export const formatDateTime = (date) =>
  new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);

export const getHeaderValue = (headers, name) => {
  if (typeof headers?.get === "function") return headers.get(name);
  const key = Object.keys(headers || {}).find((headerName) => headerName.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : null;
};

export const getFetchOperationName = (body) => {
  if (typeof body === "string") {
    try {
      return JSON.parse(body).operationName || "";
    } catch {
      return "";
    }
  }

  const operations = body?.get?.("operations");
  if (operations) {
    try {
      return JSON.parse(operations).operationName || "";
    } catch {
      return "";
    }
  }
  return "";
};

// transformToDate :: String -> Date
export const transformToDate = (str) => new Date(str);

// addDays :: Int -> Date -> Date
export const addDays = curry((days, date) => {
  let localMutable = new Date(date);
  localMutable.setDate(localMutable.getDate() + days);
  return localMutable;
});

// addOneDay :: Date -> Date
export const addOneDay = addDays(1);

// addMilliseconds :: Int -> Date -> Date
export const addMilliseconds = curry((milliseconds, date) => new Date(date.getTime() + milliseconds));

// subtractOneMillisecond :: Date -> Date
export const subtractOneMillisecond = addMilliseconds(-1);

// sum elements of array
export const sum = (obj) => {
  return Object.keys(obj).reduce((sum, key) => sum + parseFloat(obj[key] || 0), 0);
};

export const invalidateApolloClientCache = (client, obj, fields = null) => {
  const cacheId = client.cache.identify(obj);
  if (fields === null) client.cache.evict({ id: cacheId });
  else fields.forEach((field) => client.cache.evict({ id: cacheId, fieldName: field }));
  client.cache.gc();
};

export const getFlagName = (countryCode, flagName) => {
  if (flagName === "US")
    switch (countryCode.toLowerCase()) {
      case "gb":
        return "GB";
      case "ie":
        return "IE";
      default:
        return flagName;
    }
  return flagName;
};

export const normalizeDecimalToInvariant = (input) => {
  if (!input) return input;

  const value = input.trim();
  const lastDotIndex = value.lastIndexOf(".");
  const lastCommaIndex = value.lastIndexOf(",");
  const decimalSymbolIndex = Math.max(lastCommaIndex, lastDotIndex);

  if (decimalSymbolIndex >= 0) {
    const integerPart = value
      .substring(0, decimalSymbolIndex)
      .replace(",", "")
      .replace(".", "")
      .replace("'", "")
      .replace(" ", "");
    const decimalPart = value.substring(decimalSymbolIndex + 1);
    return `${integerPart}.${decimalPart}`;
  }
  return value.replace(",", "").replace(".", "").replace("'", "").replace(" ", "");
};
